import React, { useState, useEffect } from "react";
import Headers from '../components/Headers'
import Footer from '../components/Footer'
import DirectTransferCheckout from '../components/DirectTransferCheckout'
import { useLocation, useParams, useOutletContext } from 'react-router-dom'
import { formatCurrency, fetchExchangeRates } from "../utils/currencyUtils";
import { useDispatch, useSelector } from "react-redux";
import { fetchStoreBySlug } from "../store/reducers/homeReducer";
import { normalizeStoreName } from "../utils/storeUtils";

const Payment = () => {
    const { storeName: urlStoreName } = useParams();
    const outlet = useOutletContext();
    const isStoreMode = !!urlStoreName || !!outlet?.isStoreMode;
    const location = useLocation();
    const { fromStore } = location.state || {};
    const orderId = new URLSearchParams(location.search).get('orderId') || location.state?.orderId;
    const [currencyCode, setCurrencyCode] = useState("NGN");
    const [exchangeLoaded, setExchangeLoaded] = useState(false);
    const dispatch = useDispatch();
    const { seller } = useSelector((state) => state.home);
    const storeToPass = urlStoreName || fromStore;

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchExchangeRates().then(() => setExchangeLoaded(true));
    }, []);

    useEffect(() => {
        // Only fetch store if NOT in StoreLayout. StoreLayout handles it otherwise.
        if (storeToPass && !isStoreMode) {
            dispatch(fetchStoreBySlug(storeToPass));
        }
    }, [storeToPass, dispatch, isStoreMode]);

    // Theme logic - Only if NOT StoreMode
    const getThemeClass = () => {
        if (isStoreMode) return '';

        const themeSettings = seller?.themeSettings;
        const useCustomTheme = themeSettings?.useCustomTheme;

        // Custom theme always uses light-theme class (defaults to light colors)
        if (useCustomTheme) {
            return 'light-theme';
        }
        if (seller?.shopInfo?.theme === 'light') {
            return 'light-theme';
        }
        if (storeToPass) {
            const normalizedStore = normalizeStoreName(storeToPass);
            const cachedTheme = localStorage.getItem(`store-theme-${normalizedStore}`);
            if (cachedTheme === 'light') return 'light-theme';

            try {
                const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
                if (cachedSettings) {
                    const settings = JSON.parse(cachedSettings);
                    if (settings.useCustomTheme) return 'light-theme';
                }
            } catch (e) { }
        }
        return '';
    };
    const themeClass = getThemeClass();

    // Apply theme class to body immediately on render if we have cached theme context
    // Only if NOT StoreMode
    if (!isStoreMode && themeClass === 'light-theme') {
        document.body.classList.add('light-theme');

        // If we have cached custom theme settings, apply them now to prevent flash
        if (storeToPass) {
            try {
                const normalizedStore = normalizeStoreName(storeToPass);
                const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
                if (cachedSettings) {
                    const settings = JSON.parse(cachedSettings);
                    if (settings.useCustomTheme) {
                        const colors = settings.colors || {};
                        const setVar = (name, value) => {
                            if (value) document.body.style.setProperty(name, value);
                        };
                        const getContrastColor = (hexcolor) => {
                            if (!hexcolor || hexcolor === 'transparent') return null;
                            const hex = hexcolor.replace('#', '');
                            const r = parseInt(hex.slice(0, 2), 16);
                            const g = parseInt(hex.slice(2, 4), 16);
                            const b = parseInt(hex.slice(4, 6), 16);
                            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                            return (yiq >= 128) ? '#000000' : '#ffffff';
                        };
                        const contrastColor = getContrastColor(colors.background);
                        setVar('--store-bg', colors.background);
                        setVar('--store-text', contrastColor);
                        setVar('--store-icon', contrastColor);
                        setVar('--store-button-bg', colors.buttonBg);
                        setVar('--store-button-text', colors.buttonText);
                        setVar('--store-product-card-bg', colors.productCardBg);
                        setVar('--store-header-bg', colors.headerBg);
                        setVar('--store-footer-bg', colors.footerBg);
                        setVar('--store-border', colors.border);
                        if (settings.typography?.fontFamily) {
                            const fontFamily = settings.typography.fontFamily;
                            const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;
                            if (!document.getElementById(fontId)) {
                                const link = document.createElement('link');
                                link.id = fontId;
                                link.rel = 'stylesheet';
                                link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                                document.head.appendChild(link);
                            }
                            setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                        }
                    }
                }
            } catch (e) {
                console.error("Error applying cached theme:", e);
            }
        }
    }

    // Theme effect - Only if NOT StoreMode
    useEffect(() => {
        if (isStoreMode) return;

        if (seller?.shopInfo && storeToPass) {
            const normalizedStore = normalizeStoreName(storeToPass);
            localStorage.setItem(`store-theme-${normalizedStore}`, seller.shopInfo.theme);
            localStorage.setItem(`store-settings-${normalizedStore}`, JSON.stringify(seller.themeSettings || {}));

            const themeSettings = seller?.themeSettings;
            const useCustomTheme = themeSettings?.useCustomTheme;

            // Apply light-theme class when using custom theme or when theme is 'light'
            if (useCustomTheme || seller.shopInfo.theme === 'light') {
                document.body.classList.add('light-theme');
            } else {
                document.body.classList.remove('light-theme');
            }

            // Apply custom CSS variables when useCustomTheme is true
            if (useCustomTheme && themeSettings) {
                const colors = themeSettings.colors || {};
                const setVar = (name, value) => {
                    if (value) document.body.style.setProperty(name, value);
                };

                const getContrastColor = (hexcolor) => {
                    if (!hexcolor || hexcolor === 'transparent') return null;
                    const hex = hexcolor.replace('#', '');
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                    return (yiq >= 128) ? '#000000' : '#ffffff';
                };

                const contrastColor = getContrastColor(colors.background);
                setVar('--store-bg', colors.background);
                setVar('--store-text', contrastColor);
                setVar('--store-icon', contrastColor);
                setVar('--store-button-bg', colors.buttonBg);
                setVar('--store-button-text', colors.buttonText);
                setVar('--store-product-card-bg', colors.productCardBg);
                setVar('--store-header-bg', colors.headerBg);
                setVar('--store-footer-bg', colors.footerBg);
                setVar('--store-input-bg', themeSettings.inputs?.background || colors.inputBg);
                setVar('--store-input-text', themeSettings.inputs?.text || colors.inputText);
                setVar('--store-input-border', themeSettings.inputs?.border || colors.inputBorder);
                setVar('--store-input-focus', themeSettings.inputs?.focusRing || '#000000');
                setVar('--store-input-radius', `${themeSettings.inputs?.radius || 20}px`);
                setVar('--store-select-bg', themeSettings.selects?.background || colors.inputBg);
                setVar('--store-select-text', themeSettings.selects?.text || colors.inputText);
                setVar('--store-select-border', themeSettings.selects?.border || colors.inputBorder);
                setVar('--store-select-focus', themeSettings.selects?.focusRing || '#000000');
                setVar('--store-select-radius', `${themeSettings.selects?.radius || 20}px`);
                setVar('--store-border', colors.border);
                setVar('--store-muted', colors.muted);
                setVar('--store-glass', colors.glass);

                // Load and apply Google Font
                if (themeSettings.typography?.fontFamily) {
                    const fontFamily = themeSettings.typography.fontFamily;
                    const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;

                    if (!document.getElementById(fontId)) {
                        const link = document.createElement('link');
                        link.id = fontId;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                        document.head.appendChild(link);
                    }

                    setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                    document.body.style.fontFamily = `"${fontFamily}", sans-serif`;
                }
            }
        }

        // Cleanup when unmounting or changing stores
        return () => {
            document.body.classList.remove('light-theme');
        };
    }, [seller, storeToPass, isStoreMode]);

    return (
        <div className={`min-h-screen bg-[var(--store-bg)] transition-colors duration-300 ${!isStoreMode ? themeClass : ''}`}>
            {!isStoreMode && <Headers />}
            <section className="bg-[var(--store-bg)]">
                <div className='w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto py-16'>
                    <div className='flex justify-center'>
                        <div className='w-full max-w-2xl'>
                            <div className="text-[var(--store-text)]"><DirectTransferCheckout orderId={orderId} /></div>
                        </div>
                    </div>
                </div>
            </section>
            {!isStoreMode && <Footer />}
        </div>
    )
}

export default Payment